import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarBlank, Plus, X, Clock, Trash, Copy, YoutubeLogo, InstagramLogo, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PLATFORM_ICON = { youtube: YoutubeLogo, instagram: InstagramLogo };
const PLATFORM_COLOR = { youtube: "#EAB308", instagram: "#00E5FF" };

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
function fmtMonth(d) { return d.toLocaleDateString("en-US", { month: "long", year: "numeric" }); }
function ymd(d) { return d.toISOString().slice(0, 10); }

export default function Calendar() {
  const [cursor, setCursor] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [form, setForm] = useState({
    title: "", caption: "", hashtags: "", platform: "youtube", time: "18:00",
  });
  const [optimalHours, setOptimalHours] = useState([]);

  const load = async () => {
    const start = startOfMonth(cursor).toISOString();
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1).toISOString();
    const { data } = await api.get("/calendar/posts", { params: { start, end } });
    setPosts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cursor]);

  useEffect(() => {
    api.get(`/calendar/optimal-times?platform=${form.platform}`).then(r => setOptimalHours(r.data.hours));
  }, [form.platform]);

  const monthDays = daysInMonth(cursor);
  const firstWeekday = startOfMonth(cursor).getDay();
  const grid = [
    ...Array(firstWeekday).fill(null),
    ...Array(monthDays).fill(null).map((_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1)),
  ];

  const postsByDay = posts.reduce((acc, p) => {
    const k = p.scheduled_at?.slice(0, 10);
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});

  const openCreate = (day) => {
    setSelectedDay(day);
    setForm(f => ({ ...f, title: "", caption: "", hashtags: "" }));
    setShowCreate(true);
  };

  const create = async () => {
    if (!selectedDay || !form.title.trim()) { toast.error("Title required"); return; }
    const [h, m] = form.time.split(":");
    const dt = new Date(selectedDay);
    dt.setUTCHours(Number(h), Number(m), 0, 0);
    const hashtags = form.hashtags.split(/[,\s]+/).map(t => t.replace(/^#/, "").trim()).filter(Boolean);
    await api.post("/calendar/posts", {
      title: form.title, caption: form.caption, hashtags,
      platform: form.platform, scheduled_at: dt.toISOString(),
    });
    toast.success("Post scheduled");
    setShowCreate(false);
    load();
  };

  const del = async (id) => {
    await api.delete(`/calendar/posts/${id}`);
    load();
  };

  const exportIcs = async () => {
    try {
      const resp = await api.get("/calendar/export.ics", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url; a.download = "clipforge-calendar.ics"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Calendar exported");
    } catch {
      toast.error("Export failed");
    }
  };

  // Drag-and-drop reschedule (optimistic)
  const [dragId, setDragId] = useState(null);
  const onDragStart = (id) => (e) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (newDate) => async (e) => {
    e.preventDefault();
    if (!dragId) return;
    const id = dragId;
    setDragId(null);
    const post = posts.find(p => p.id === id);
    if (!post) return;
    const orig = new Date(post.scheduled_at);
    const target = new Date(newDate);
    target.setUTCHours(orig.getUTCHours(), orig.getUTCMinutes(), 0, 0);
    if (target.toISOString() === post.scheduled_at) return;

    // Optimistic update — patch local state immediately, rollback on failure
    const prev = posts;
    const next = posts.map(p => p.id === id ? { ...p, scheduled_at: target.toISOString() } : p);
    setPosts(next);
    try {
      await api.patch(`/calendar/posts/${id}`, { scheduled_at: target.toISOString() });
      toast.success("Rescheduled");
    } catch {
      setPosts(prev);
      toast.error("Reschedule failed — reverted");
    }
  };

  const copyCaption = (p) => {
    const tags = (p.hashtags || []).map(t => "#" + t).join(" ");
    navigator.clipboard.writeText(`${p.caption}\n\n${tags}`);
    toast.success("Caption copied — paste into your post");
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[#FF3B6B] text-sm font-mono">// CONTENT CALENDAR</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Plan your posting cadence</h1>
          <p className="text-white/50 mt-1">Schedule across YouTube · Instagram with AI-suggested optimal times.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportIcs} className="px-3 py-1.5 glass rounded-lg text-sm hover:bg-white/5 flex items-center gap-2" data-testid="cal-export-ics">
            <Download size={14} /> Export .ics
          </button>
          <div className="flex items-center gap-2 glass rounded-lg p-1">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="px-3 py-1.5 hover:bg-white/5 rounded text-sm" data-testid="cal-prev">‹</button>
            <span className="px-4 py-1.5 font-medium text-sm min-w-[160px] text-center" data-testid="cal-month-label">{fmtMonth(cursor)}</span>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="px-3 py-1.5 hover:bg-white/5 rounded text-sm" data-testid="cal-next">›</button>
            <button onClick={() => setCursor(new Date())} className="px-3 py-1.5 text-xs ml-1 text-white/60 hover:text-white" data-testid="cal-today">Today</button>
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-7 gap-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} className="text-xs uppercase tracking-wider text-white/40 font-mono px-2 py-1">{d}</div>
        ))}
        {grid.map((day, i) => {
          if (!day) return <div key={i} className="aspect-square" />;
          const k = ymd(day);
          const dayPosts = postsByDay[k] || [];
          const isToday = ymd(day) === ymd(new Date());
          return (
            <motion.div
              key={k}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.005 }}
              onClick={() => openCreate(day)}
              onDragOver={onDragOver}
              onDrop={onDrop(day)}
              className={`relative aspect-square rounded-lg text-left p-2 transition-all border cursor-pointer ${
                isToday ? "border-[#FF3B6B] bg-[#FF3B6B]/5" : "border-white/10 hover:border-white/30 bg-white/[0.02]"
              }`}
              data-testid={`cal-day-${k}`}
            >
              <div className={`text-xs font-mono ${isToday ? "text-[#FF3B6B]" : "text-white/60"}`}>{day.getDate()}</div>
              <div className="mt-1 space-y-1">
                {dayPosts.slice(0, 2).map(p => {
                  const Icon = PLATFORM_ICON[p.platform];
                  return (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={onDragStart(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] truncate rounded px-1 py-0.5 flex items-center gap-1 cursor-grab active:cursor-grabbing"
                      style={{ background: `${PLATFORM_COLOR[p.platform]}15`, color: PLATFORM_COLOR[p.platform] }}
                      data-testid={`cal-chip-${p.id}`}
                    >
                      {Icon && <Icon size={9} weight="fill" />} {p.title}
                    </div>
                  );
                })}
                {dayPosts.length > 2 && (
                  <div className="text-[10px] text-white/50">+{dayPosts.length - 2} more</div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Upcoming list */}
      <h2 className="mt-12 text-lg font-semibold flex items-center gap-2">
        <Clock className="text-[#FF3B6B]" /> Upcoming this month
      </h2>
      <div className="mt-4 space-y-2">
        {posts.length === 0 && (
          <div className="glass rounded-xl p-8 text-center text-white/40 border-dashed">
            <CalendarBlank size={40} className="mx-auto mb-2 text-white/20" />
            <p>No posts scheduled. Click a day to add one.</p>
          </div>
        )}
        {posts.map(p => {
          const Icon = PLATFORM_ICON[p.platform];
          return (
            <div key={p.id} className="glass rounded-xl p-4 flex items-center gap-4 hover:border-white/30 transition-all" data-testid={`cal-post-${p.id}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${PLATFORM_COLOR[p.platform]}15`, color: PLATFORM_COLOR[p.platform] }}>
                {Icon && <Icon size={20} weight="fill" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.title}</p>
                <p className="text-xs text-white/50 mt-0.5">{new Date(p.scheduled_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })} · {p.platform}</p>
              </div>
              <button onClick={() => copyCaption(p)} className="text-white/40 hover:text-white p-2" data-testid={`copy-caption-${p.id}`}><Copy size={14} /></button>
              <button onClick={() => del(p.id)} className="text-white/30 hover:text-[#FF3B6B] p-2" data-testid={`delete-post-${p.id}`}><Trash size={14} /></button>
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      {showCreate && selectedDay && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setShowCreate(false)} data-testid="cal-create-modal">
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl p-8 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold tracking-tighter">Schedule for {selectedDay.toLocaleDateString()}</h2>
              <button onClick={() => setShowCreate(false)}><X /></button>
            </div>
            <div className="space-y-4">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Post title" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="cal-title-input" />
              <textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={3} placeholder="Caption" className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="cal-caption-input" />
              <input value={form.hashtags} onChange={(e) => setForm({ ...form, hashtags: e.target.value })} placeholder="hashtags (space or comma separated)" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="cal-hashtags-input" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider">Platform</label>
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    {["youtube", "instagram"].map(p => (
                      <button key={p} onClick={() => setForm({ ...form, platform: p })} className={`py-2 rounded text-xs capitalize ${form.platform === p ? "bg-[#FF3B6B]" : "bg-white/5 border border-white/10"}`} data-testid={`cal-platform-${p}`}>{p}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/60 uppercase tracking-wider">Time</label>
                  <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="cal-time-input" />
                </div>
              </div>
              {optimalHours.length > 0 && (
                <div className="text-xs text-white/50">
                  <span className="text-[#00E5FF] font-mono">AI suggested:</span>{" "}
                  {optimalHours.map(h => (
                    <button key={h} onClick={() => setForm({ ...form, time: `${String(h).padStart(2, "0")}:00` })} className="inline-block mx-0.5 px-1.5 py-0.5 rounded bg-white/5 hover:bg-[#FF3B6B]/10 hover:text-[#FF3B6B]" data-testid={`cal-suggest-${h}`}>
                      {String(h).padStart(2, "0")}:00
                    </button>
                  ))}
                </div>
              )}
              <button onClick={create} className="w-full btn-primary py-3 rounded-lg font-semibold" data-testid="cal-save-btn">Schedule post</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
